import React, {Component, ReactNode, SyntheticEvent} from 'react';
import {findDOMNode} from 'react-dom';
import {injectIntl} from 'react-intl';
import {Button, ButtonPrimary} from '@homeaway/react-buttons';
import {TrackingContext} from '@homeaway/react-event-tracking';
import BedroomsFilter from '../BedroomsFilter/BedroomsFilter';
import BathroomsFilter from '../BathroomsFilter/BathroomsFilter';
import {getFreeCancellationFilters} from '../../utils/filters';
import keyboardConstants from '../../constants/keyboard';
import {renderFilterGroups} from '../MoreFilters/MoreFilters';
import seeResultsMessage from '../SeeResultsMessage/SeeResultsMessage';
import PriceFilter from '../PriceFilter/PriceFilter';
import FreeCancellationFilter from '../FreeCancellationFilter/FreeCancellationFilter';

import {newArrayFromFilters, cannotFilter} from '../../utils/filters';
import {triggerEdapEventForCheckbox} from '../../utils/edap';

import type {BoundsType} from '../PriceFilter/PriceFilter';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import type {IntlShape} from 'react-intl';

import {Refinement} from '../FilterGroup/FilterGroup';

import './MoreFiltersPanel.less';

type InjectIntlProps = {
    intl: IntlShape;
};

export type Props = {
    canFit3Columns: boolean;
    canFit4Columns: boolean;
    currency: string;
    filter: string[];
    hasDates: boolean;
    inputMaxBedrooms: number | null;
    inputMaxNightlyPrice: number | null;
    inputMinBedrooms: number | null;
    inputMinNightlyPrice: number | null;
    inputParamsAreChanged: boolean;
    isMobile: boolean;
    maxBathrooms: number | null;
    minBathrooms: number | null;
    priceInputValuesAreValid: boolean;
    selectedFilters: string[];
    topBannersHeight: number;
    filterGroups: FilterGroupType[];
    resultsCount: number | null;
    onApplyFilters: () => void;
    onCancel: () => void;
    onInputParamsRangeFilterChange: (SearchParamsType) => void;
    updateInputParams: (SearchParamsType) => void;
    updateFilterCheck: (FilterCheckType) => void;
    updatePriceInputValuesValidity: Function;
    updateMultipleFilterCheck: (updatedFilters: Refinement[]) => void;
} & InjectIntlProps;

export class MoreFiltersPanel extends Component<Props> {
    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
        /* focus content for keyboard input */
        if (this.moreFiltersContentRef && this.moreFiltersContentRef.current) {
            this.moreFiltersContentRef.current.focus();
        }
        /* adjust panel's position */
        this.adjustPanelPosition(this.props.topBannersHeight);
    }

    componentDidUpdate(prevProps: Props) {
        const {topBannersHeight} = this.props;

        /* adjust panel's position */
        if (prevProps.topBannersHeight !== topBannersHeight) {
            this.adjustPanelPosition(topBannersHeight);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    moreFiltersContentRef: {current: null | HTMLDivElement} = React.createRef();

    adjustPanelPosition = (topBannersHeight: number) => {
        const node = findDOMNode(this);

        if (
            node instanceof Element &&
            node.parentNode &&
            node.parentNode instanceof HTMLElement &&
            parseInt(window.getComputedStyle(node.parentNode).marginTop, 10) !==
                topBannersHeight
        ) {
            node.parentNode.style.setProperty(
                'margin-top',
                `${topBannersHeight}px`
            );
        }
    };

    handleKeyDown = (e: KeyboardEvent) => {
        if (e.which === keyboardConstants.ESC) {
            this.props.onApplyFilters();
        }
    };

    handleMoreFilterCheck = (event: SyntheticEvent<HTMLInputElement>) => {
        if (!event.currentTarget) {
            return;
        }

        triggerEdapEventForCheckbox(event, this.context);
        this.props.updateFilterCheck(event.currentTarget);
    };

    getColumns = (children: Array<ReactNode>, columns = 2) => {
        const arr: any = [];

        for (let i = 0; i < children.length; i++) {
            const modulus = i % columns;
            arr[modulus] = arr[modulus] || [];
            arr[modulus].push(children[i]);
        }
        return arr;
    };

    handlePriceChange = (bounds: BoundsType) => {
        const {updateInputParams} = this.props;
        const values = {
            minNightlyPrice: bounds[0] || 0,
            maxNightlyPrice: bounds[1],
        };
        updateInputParams(values);
    };

    renderPrimaryFilters = (className = '') => {
        const {
            currency,
            hasDates,
            filter,
            inputMaxNightlyPrice,
            inputMinNightlyPrice,
            onInputParamsRangeFilterChange,
            inputMinBedrooms,
            inputMaxBedrooms,
            priceInputValuesAreValid,
            updatePriceInputValuesValidity,
            minBathrooms,
            maxBathrooms,
            filterGroups,
            updateMultipleFilterCheck,
        } = this.props;
        const cancellationFilters = getFreeCancellationFilters(filterGroups);
        return (
            <div
                key="primaryFiltersMoreFiltersPanel"
                className={className}
                role="presentation"
            >
                <FreeCancellationFilter
                    filters={cancellationFilters}
                    selectedFilters={filter}
                    onMultipleFilterToggle={updateMultipleFilterCheck}
                    oldExperience
                    className="MoreFiltersPanel__item"
                />
                <PriceFilter
                    currency={currency}
                    hasDates={hasDates}
                    onPriceChange={this.handlePriceChange}
                    minPrice={inputMinNightlyPrice}
                    maxPrice={inputMaxNightlyPrice}
                    className="MoreFiltersPanel__item"
                    priceInputValuesAreValid={priceInputValuesAreValid}
                    updatePriceInputValuesValidity={
                        updatePriceInputValuesValidity
                    }
                />
                <BedroomsFilter
                    onChange={onInputParamsRangeFilterChange}
                    minBedrooms={inputMinBedrooms || 0}
                    maxBedrooms={inputMaxBedrooms}
                    className="MoreFiltersPanel__item"
                />
                <BathroomsFilter
                    onChange={onInputParamsRangeFilterChange}
                    minBathrooms={minBathrooms || 0}
                    maxBathrooms={maxBathrooms}
                    className="MoreFiltersPanel__item"
                />
            </div>
        );
    };

    /* eslint-disable complexity */
    render() {
        const {
            handleMoreFilterCheck,
            props: {
                canFit3Columns,
                canFit4Columns,
                filter,
                filterGroups,
                intl,
                isMobile,
                onApplyFilters,
                priceInputValuesAreValid,
                resultsCount,
                selectedFilters,
                onCancel,
            },
        } = this;
        const submitDisabled =
            cannotFilter(resultsCount) || !priceInputValuesAreValid;

        const cancelLabel = intl.formatMessage({
            id: 'formModalCloseText',
            description: 'cancel button',
            defaultMessage: 'Cancel',
        });

        const inputFilters = newArrayFromFilters({filter});
        const appliedFilters = selectedFilters;

        const childrenToRender = [
            ...renderFilterGroups(appliedFilters, {
                filterGroups,
                className: 'MoreFiltersPanelGroup',
                handleChecked: handleMoreFilterCheck,
                checked: inputFilters,
                tooltipOptions: {
                    isMobile,
                },
            }),
        ];

        const columns = this.getColumns(
            childrenToRender,
            canFit4Columns ? 3 : 2
        );

        /* If we cannot fit 3 columns, we need to add the primary filters to the filter groups for the 2 columns */
        if (!canFit3Columns && columns[0]) {
            columns[0].unshift(this.renderPrimaryFilters());
        }

        return (
            <div
                role="presentation"
                className="MoreFiltersPanel"
                data-wdio="MoreFiltersPanel"
                key={'MoreFiltersPanel'}
            >
                <div
                    role="presentation"
                    className="MoreFiltersPanel__content"
                    ref={this.moreFiltersContentRef}
                    tabIndex={0}
                >
                    {canFit3Columns &&
                        this.renderPrimaryFilters(
                            'MoreFiltersPanel__column MoreFiltersPanel__column--left'
                        )}
                    {columns.map((component, index) => (
                        <div
                            role="presentation"
                            className="MoreFiltersPanel__column"
                            key={`MoreFiltersPanel-column-${index}`}
                        >
                            {component}
                        </div>
                    ))}
                </div>
                <div
                    role="presentation"
                    className={'MoreFiltersPanel__buttons--container'}
                >
                    <nav className="MoreFiltersPanel__buttons">
                        <Button
                            id="moreFiltersPanel__btn--cancel"
                            label={cancelLabel}
                            onClick={onCancel}
                        />
                        <ButtonPrimary
                            id="moreFiltersPanel__btn--submit"
                            disabled={submitDisabled}
                            label={seeResultsMessage({
                                intl: this.props.intl,
                                resultsCount,
                            })}
                            onClick={onApplyFilters}
                        />
                    </nav>
                </div>
            </div>
        );
    }
    /* eslint-enable complexity */
}

MoreFiltersPanel.contextType = TrackingContext;

export default injectIntl(MoreFiltersPanel);
